/**
 * Pure onboarding step-gating logic — the corpus-state -> step function the
 * brief asks for, kept framework-free (no React, no fetch) so it is
 * unit-testable standalone, same house idiom jobBuckets.ts/stageMove.ts/
 * dragRehome.ts already establish for this app's pure modules.
 *
 * Diagram: docs/diagrams/c3-onboarding-2026-07-04.svg (the 5 numbered nodes
 * below map 1:1 onto this module's OnboardingStep values). This is a REWORK
 * of B-1's Onboarding.tsx 3-step (welcome/upload/handoff) local-only state
 * machine — see that file's own header, which named the real corpus import +
 * first Mira session flow as explicitly NOT B-1's scope ("first-run corpus
 * bootstrap" in throughline-contract.md's own data-bindings list).
 *
 * THE LAW THIS MODULE ENFORCES: completion is never a client-only flag once
 * server truth exists. `resolveOnboardingStep` takes the corpus read (roles
 * count — the same GET /corpus/roles this app's useCorpusCanvas already
 * calls) as its ONE source of "does this person have a real corpus yet," and
 * a local "have they seen this before" flag only for the pre-signup demo
 * step, which server truth cannot answer (a stranger who hasn't signed in yet
 * has no server session to read). roles > 0 always wins over any stored
 * localStorage completion flag — a returning user who imported their corpus
 * on another device/session skips straight past the interview here too,
 * because the server, not a client flag, is what actually knows.
 */

export type OnboardingStep =
  | 'demo' // 1 · labeled demo card (pre-signup, no mic)
  | 'one-question' // 2 · one question, voice or text
  | 'interview' // 3 · build the rest (links into Refine)
  | 'first-output' // 4 · first real output (links into Get Found / Profiles)
  | 'watchlist-seed' // 5 · the watchlist seed + "she keeps looking"
  | 'done'; // onboarding overlay retires; the tab bar shows

export const ONBOARDING_STEPS: OnboardingStep[] = [
  'demo',
  'one-question',
  'interview',
  'first-output',
  'watchlist-seed',
  'done',
];

export interface CorpusStateSnapshot {
  /** GET /corpus/roles' result length — the ONLY server-truth signal this
   *  module reads. Null while the read is still in flight (the caller must
   *  not resolve a step from an unknown corpus state — see
   *  resolveOnboardingStep's null-handling below). */
  roleCount: number | null;
}

export interface OnboardingProgress {
  /** The furthest step the user has explicitly advanced past, persisted
   *  client-side (localStorage) so a browser refresh mid-flow resumes rather
   *  than restarting at 'demo'. Independent of server truth — this is
   *  "where they left off," not "what's actually true," which is why
   *  roleCount still overrides it below. */
  furthestStepIndex: number;
  /** Explicit skip — the existing Onboarding.tsx's onSkip affordance,
   *  preserved: retires the overlay outright regardless of corpus state. */
  skipped: boolean;
}

export const INITIAL_ONBOARDING_PROGRESS: OnboardingProgress = {
  furthestStepIndex: 0,
  skipped: false,
};

function stepIndex(step: OnboardingStep): number {
  return ONBOARDING_STEPS.indexOf(step);
}

/**
 * The single corpus-state -> step function. Precedence, highest first:
 *   1. explicit skip -> 'done' (the user's own choice always wins)
 *   2. roleCount unknown (still loading, or the user isn't signed in yet) ->
 *      whatever the LOCAL progress says (never blocks the pre-signup demo
 *      step on a corpus read that has no session to run against yet)
 *   3. roleCount > 0 (server truth: a real corpus already exists) -> skip
 *      straight to 'watchlist-seed' — the one step that still has genuine
 *      value for a returning user (the loop that "keeps looking"), never
 *      re-showing the demo card or the one-question ask to someone who
 *      already has real data
 *   4. roleCount === 0 -> the LOCAL progress (resumable, skippable per-step)
 */
export function resolveOnboardingStep(
  corpus: CorpusStateSnapshot,
  progress: OnboardingProgress,
): OnboardingStep {
  if (progress.skipped) return 'done';

  if (corpus.roleCount !== null && corpus.roleCount > 0) {
    // A real corpus already exists — server truth overrides ANY stored local
    // progress (including a prior 'done'/skip from before the corpus existed,
    // and including a furthestStepIndex that never reached this far). The
    // watchlist-seed step is still worth showing once, since it is additive
    // (seeding search terms) rather than something the corpus already implies.
    const furthest = ONBOARDING_STEPS[progress.furthestStepIndex] ?? 'demo';
    if (furthest === 'watchlist-seed' || furthest === 'done') return furthest;
    return 'watchlist-seed';
  }

  return ONBOARDING_STEPS[progress.furthestStepIndex] ?? 'demo';
}

/** Advance progress to (at least) `step` — never regresses furthestStepIndex
 *  if the caller passes an earlier step than already reached (e.g. a user who
 *  navigates back to re-read the demo card doesn't lose their resumable
 *  position). Pure — returns a new object, never mutates. */
export function advanceOnboardingProgress(
  progress: OnboardingProgress,
  step: OnboardingStep,
): OnboardingProgress {
  const target = stepIndex(step);
  if (target < 0) return progress;
  return {
    ...progress,
    furthestStepIndex: Math.max(progress.furthestStepIndex, target),
  };
}

/** The explicit skip — retires the overlay regardless of corpus state or
 *  furthest step reached (mirrors Onboarding.tsx's existing onSkip). */
export function skipOnboarding(progress: OnboardingProgress): OnboardingProgress {
  return { ...progress, skipped: true };
}

// =============================================================================
// localStorage persistence — the "completion state persisted client-side"
// half of the brief's law; resolveOnboardingStep's roleCount precedence above
// is the "inferred from server truth" half. Mirrors App.tsx's existing
// ONBOARDING_SEEN_KEY best-effort try/catch discipline (private browsing /
// storage-disabled contexts degrade to "always show onboarding" rather than
// throwing).
// =============================================================================

const PROGRESS_KEY = 'throughline:onboarding-progress:v1';

/** True in any real browser and in a jsdom/happy-dom test environment; false
 *  in this repo's actual vitest run (node environment, no DOM globals) and in
 *  any other non-browser caller (SSR, a future worker context). Checked
 *  explicitly rather than relying on the try/catch alone, so a missing
 *  `window` reads as the SAME honest "nothing persisted yet" as a real
 *  storage failure, never a thrown ReferenceError. */
function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readOnboardingProgress(): OnboardingProgress {
  if (!hasWindow()) return INITIAL_ONBOARDING_PROGRESS;
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    if (!raw) return INITIAL_ONBOARDING_PROGRESS;
    const parsed = JSON.parse(raw) as Partial<OnboardingProgress>;
    const furthestStepIndex =
      typeof parsed.furthestStepIndex === 'number' &&
      parsed.furthestStepIndex >= 0 &&
      parsed.furthestStepIndex < ONBOARDING_STEPS.length
        ? parsed.furthestStepIndex
        : 0;
    return { furthestStepIndex, skipped: parsed.skipped === true };
  } catch {
    return INITIAL_ONBOARDING_PROGRESS;
  }
}

export function writeOnboardingProgress(progress: OnboardingProgress): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    // Best-effort only, same posture as App.tsx's markOnboarded — worst case
    // onboarding resumes from 'demo' next session instead of where they left off.
  }
}
